import React from 'react'

import { AppSvg, FormattedCurrency, FormattedHTMLMessage, newIcons, Spinner, Tooltip } from '@web/walter-ui-kit'
import { GetCard, GetTopupCommissionsReturn } from '@web/walter-lib/api'
import cn from 'classnames'
import s from './s.module.css'
import { lang } from './lang'
import { useBaseCurrency } from '~hooks/useBaseCurrency'
import { useWalletRateConvert } from '~hooks/useWalletRateConvert'

interface Props {
    isLoading: boolean
    isAmountError: boolean
    data?: GetTopupCommissionsReturn
    card?: GetCard
}

// eslint-disable-next-line complexity,max-lines-per-function
function Balances({ isLoading, data, card, isAmountError }: Props) {
    const topupFees = data?.fee || card?.fees?.topup

    const currencyCode = data?.fee?.currency || card?.currency || ''
    const { calculatedBaseCurrency: baseCurrency } = useBaseCurrency()

    const amountToReceiveBase = useWalletRateConvert(
        currencyCode,
        baseCurrency,
        data?.calculated?.amount_to_receive || '0'
    )

    const feeAmountBase = useWalletRateConvert(currencyCode, baseCurrency, data?.calculated?.fee_amount || '0')

    const amountToPayBase = useWalletRateConvert(currencyCode, baseCurrency, data?.calculated?.amount_to_pay || '0')

    const showBase = currencyCode !== baseCurrency

    return (
        <div className={s.balances}>
            <div className={s.balanceRow}>
                <FormattedHTMLMessage {...lang.toCard} className={s.balance} />
                <div className={s.value}>
                    {isLoading ? (
                        <Spinner size={12} />
                    ) : (
                        <div className={s.amountContainer}>
                            {showBase && (
                                <span className={s.base}>
                                    ~
                                    <FormattedCurrency
                                        maxPrecision={2}
                                        value={amountToReceiveBase}
                                        currency={baseCurrency}
                                        dataPointer="total_base"
                                    />
                                </span>
                            )}
                            <FormattedCurrency
                                maxPrecision={2}
                                value={data?.calculated?.amount_to_receive || '0'}
                                currency={data?.fee?.currency || card?.currency || ''}
                                dataPointer="total"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className={s.balanceRow}>
                <FormattedHTMLMessage {...lang.topUpFee} className={s.balance} />
                <div className={s.value}>
                    {isLoading ? (
                        <Spinner size={12} />
                    ) : (
                        <div className={s.topUpAlert}>
                            <div className={s.amountContainer}>
                                {showBase && (
                                    <span className={s.base}>
                                        ~
                                        <FormattedCurrency
                                            maxPrecision={2}
                                            value={feeAmountBase}
                                            currency={baseCurrency}
                                            dataPointer="fee_amount_base"
                                        />
                                    </span>
                                )}
                                <FormattedCurrency
                                    maxPrecision={2}
                                    value={data?.calculated?.fee_amount || '0'}
                                    currency={data?.fee?.currency || card?.currency || ''}
                                    dataPointer="fee_amount"
                                />
                            </div>
                            <Tooltip
                                dataPointer="tooltip"
                                placement="bottom"
                                content={
                                    (topupFees?.fixed || topupFees?.percent) && (
                                        <div className={s.middleRow}>
                                            <FormattedHTMLMessage {...lang.topUpFee} className={s.middleTitle} />
                                            <div className={s.fee}>
                                                <span className={s.middleText}>
                                                    <FormattedCurrency
                                                        maxPrecision={2}
                                                        value={topupFees?.fixed}
                                                        currency={topupFees?.currency}
                                                        dataPointer="min_fee"
                                                    />
                                                    {topupFees?.fixed && topupFees?.percent ? ' + ' : undefined}
                                                    {topupFees?.percent}
                                                    {topupFees?.percent ? '%' : ''}
                                                </span>
                                                <span className={s.minMaxText}>
                                                    {topupFees?.min ? (
                                                        <>
                                                            <FormattedHTMLMessage {...lang.min} />{' '}
                                                            <FormattedCurrency
                                                                maxPrecision={2}
                                                                value={topupFees?.min}
                                                                currency={topupFees?.currency}
                                                                dataPointer="min_fee"
                                                            />
                                                        </>
                                                    ) : undefined}
                                                    {topupFees?.min && topupFees?.max ? ', ' : undefined}
                                                    {topupFees?.max ? (
                                                        <>
                                                            <FormattedHTMLMessage {...lang.max} />{' '}
                                                            <FormattedCurrency
                                                                maxPrecision={2}
                                                                value={topupFees?.max}
                                                                currency={topupFees?.currency}
                                                                dataPointer="max_fee"
                                                            />
                                                        </>
                                                    ) : undefined}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                }
                            >
                                <AppSvg svgName={newIcons.ic16Alert} path="new-icons" size={16} className={s.alert} />
                            </Tooltip>
                        </div>
                    )}
                </div>
            </div>
            <div className={s.balanceRow}>
                <FormattedHTMLMessage {...lang.fromWallet} className={s.balance} />
                <div className={cn(s.value, isAmountError && s.error)}>
                    {isLoading ? (
                        <Spinner size={12} />
                    ) : (
                        <div className={s.amountContainer}>
                            {showBase && (
                                <span className={s.base}>
                                    ~
                                    <FormattedCurrency
                                        maxPrecision={2}
                                        value={amountToPayBase}
                                        currency={baseCurrency}
                                        dataPointer="amount_base"
                                    />
                                </span>
                            )}
                            <FormattedCurrency
                                maxPrecision={2}
                                value={data?.calculated?.amount_to_pay || '0'}
                                currency={data?.fee?.currency || card?.currency || ''}
                                dataPointer="amount"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Balances
